import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { BookOpen, BookmarkSimple, Trash, Plus, PencilSimple, Check, X } from "@phosphor-icons/react";

export function useCatalog(enabled = true) {
  const [items, setItems] = useState([]);
  const load = () => api.get("/service-catalog").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { if (enabled) load(); }, [enabled]);
  return { items, reload: load };
}

export function CatalogPicker({ items, types = [], onPick }) {
  const label = (k) => types.find((t) => t.key === k)?.label || "";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button data-testid="catalog-picker-btn" variant="outline" size="sm" className="rounded-sm"><BookOpen size={14} className="mr-1" /> Ajouter depuis le catalogue</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="rounded-sm max-h-80 overflow-y-auto w-96">
        <DropdownMenuLabel className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Services prêts à l'emploi</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 && <div className="px-2 py-3 text-sm text-muted-foreground">Catalogue vide — enregistrez une ligne avec l'icône signet, ou gérez le catalogue dans Paramètres → Contrats.</div>}
        {items.map((it) => (
          <DropdownMenuItem key={it.id} data-testid={`catalog-item-${it.id}`} onClick={() => onPick(it)} className="flex items-center justify-between gap-3">
            <span className="truncate"><span className="text-[10px] uppercase tracking-wide text-muted-foreground mr-1.5">{label(it.type)}</span>{it.description}</span>
            <span className="tabular-nums text-xs text-muted-foreground whitespace-nowrap">{money(it.unit_price)}/mois{it.one_time_fee ? ` · ${money(it.one_time_fee)} unique` : ""}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function SaveToCatalogButton({ service, onSaved }) {
  const save = async () => {
    if (!service.description) { toast.error("Ajoutez une description avant d'enregistrer dans le catalogue."); return; }
    try {
      await api.post("/service-catalog", { description: service.description, type: service.type || "", unit_price: Number(service.unit_price) || 0, one_time_fee: Number(service.one_time_fee) || 0, quantity: Number(service.quantity) || 1 });
      toast.success("Ajouté au catalogue"); onSaved?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  return <Button type="button" variant="ghost" size="icon" className="h-9 w-9 hover:text-primary" title="Enregistrer dans le catalogue" data-testid="save-to-catalog-btn" onClick={save}><BookmarkSimple size={16} /></Button>;
}

export function CatalogManager({ types = [] }) {
  const { items, reload } = useCatalog();
  const empty = { description: "", type: "", unit_price: "", one_time_fee: "", quantity: 1 };
  const [draft, setDraft] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState(empty);
  const label = (k) => types.find((t) => t.key === k)?.label || "—";
  const norm = (d) => ({ description: d.description, type: d.type || "", unit_price: Number(d.unit_price) || 0, one_time_fee: Number(d.one_time_fee) || 0, quantity: Number(d.quantity) || 1 });

  const add = async () => {
    if (!draft.description) { toast.error("Description requise"); return; }
    try { await api.post("/service-catalog", norm(draft)); setDraft(empty); toast.success("Service ajouté au catalogue"); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  const saveEdit = async () => { try { await api.put(`/service-catalog/${editId}`, norm(edit)); setEditId(null); toast.success("Catalogue mis à jour"); reload(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (id) => { try { await api.delete(`/service-catalog/${id}`); reload(); } catch (e) { toast.error(apiError(e)); } };

  const TypeSelect = ({ value, onChange, testid }) => (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="rounded-sm"><SelectValue placeholder="Type" /></SelectTrigger>
      <SelectContent>{types.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
    </Select>
  );
  const GRID = "grid grid-cols-2 md:grid-cols-[150px_1fr_110px_110px_80px] gap-2 items-center";

  return (
    <div className="border border-border bg-card rounded-sm p-6 space-y-4" data-testid="catalog-manager">
      <div>
        <h3 className="font-heading font-bold">Catalogue de services</h3>
        <p className="text-sm text-muted-foreground">Lignes de service prêtes à l'emploi (type, description, prix mensuel, frais uniques) ajoutables en un clic dans un contrat.</p>
      </div>
      <div className={`hidden md:grid md:grid-cols-[150px_1fr_110px_110px_80px] gap-2 px-1 text-[10px] uppercase tracking-[0.12em] text-muted-foreground`}><span>Type</span><span>Description</span><span className="text-right">Prix / mois</span><span className="text-right">Frais unique</span><span></span></div>
      {items.map((it) => (
        <div key={it.id} className={GRID} data-testid={`catalog-row-${it.id}`}>
          {editId === it.id ? <>
            <TypeSelect value={edit.type} onChange={(v) => setEdit({ ...edit, type: v })} testid="catalog-edit-type" />
            <Input data-testid="catalog-edit-desc" className="rounded-sm" value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} />
            <Input data-testid="catalog-edit-price" type="number" step="0.01" className="rounded-sm text-right tabular-nums" value={edit.unit_price} onChange={(e) => setEdit({ ...edit, unit_price: e.target.value })} />
            <Input type="number" step="0.01" className="rounded-sm text-right tabular-nums" value={edit.one_time_fee} onChange={(e) => setEdit({ ...edit, one_time_fee: e.target.value })} />
            <div className="flex justify-end gap-1"><Button data-testid="catalog-edit-save" variant="ghost" size="icon" className="h-8 w-8 text-emerald-700" onClick={saveEdit}><Check size={16} /></Button><Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditId(null)}><X size={16} /></Button></div>
          </> : <>
            <span className="text-xs uppercase tracking-wide text-muted-foreground">{label(it.type)}</span>
            <span className="text-sm">{it.description}</span>
            <span className="text-sm text-right tabular-nums">{money(it.unit_price)}</span>
            <span className="text-sm text-right tabular-nums text-muted-foreground">{money(it.one_time_fee)}</span>
            <div className="flex justify-end gap-1">
              <Button data-testid={`catalog-edit-${it.id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditId(it.id); setEdit({ ...it }); }}><PencilSimple size={16} /></Button>
              <Button data-testid={`catalog-delete-${it.id}`} variant="ghost" size="icon" className="h-8 w-8 hover:text-destructive" onClick={() => del(it.id)}><Trash size={16} /></Button>
            </div>
          </>}
        </div>
      ))}
      <div className={`${GRID} border-t border-border pt-4`}>
        <TypeSelect value={draft.type} onChange={(v) => setDraft({ ...draft, type: v })} testid="catalog-new-type" />
        <Input data-testid="catalog-new-desc" className="rounded-sm" placeholder="Ex. Innova-Sign — licence par écran" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
        <Input data-testid="catalog-new-price" type="number" step="0.01" className="rounded-sm text-right tabular-nums" placeholder="0,00" value={draft.unit_price} onChange={(e) => setDraft({ ...draft, unit_price: e.target.value })} />
        <Input data-testid="catalog-new-onetime" type="number" step="0.01" className="rounded-sm text-right tabular-nums" placeholder="0,00" value={draft.one_time_fee} onChange={(e) => setDraft({ ...draft, one_time_fee: e.target.value })} />
        <Button data-testid="catalog-add-btn" size="sm" className="rounded-sm" onClick={add}><Plus size={14} className="mr-1" /> Ajouter</Button>
      </div>
    </div>
  );
}
